// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Target, 
  Award,
  Calendar,
  Star,
  BarChart3,
  PieChart
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

interface AffiliatePerformanceData {
  id: string;
  affiliate_id: string;
  month_year: string;
  referrals_sent: number;
  conversions: number;
  revenue_generated: number;
  commission_earned: number;
  commission_paid: boolean;
  commission_paid_date?: string;
  bonus_earned: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    id: string;
    name: string;
    email: string;
    company_name?: string;
  };
}

interface AffiliateProfile {
  id: string;
  name: string;
  email: string;
  company_name?: string;
  totalRevenue?: number;
  performance_data?: AffiliatePerformanceData[];
}

interface PerformanceStats {
  totalAffiliates: number;
  activeAffiliates: number;
  totalReferrals: number;
  totalConversions: number;
  totalRevenue: number;
  totalCommissions: number;
  averageConversionRate: number;
  topPerformers: AffiliateProfile[];
}

const AffiliatePerformance: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [performanceData, setPerformanceData] = useState<AffiliatePerformanceData[]>([]);
  const [affiliates, setAffiliates] = useState<AffiliateProfile[]>([]);
  const [stats, setStats] = useState<PerformanceStats>({
    totalAffiliates: 0,
    activeAffiliates: 0,
    totalReferrals: 0,
    totalConversions: 0,
    totalRevenue: 0,
    totalCommissions: 0,
    averageConversionRate: 0,
    topPerformers: []
  });

  const timePeriods = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'year_to_date', label: 'Year to Date' }
  ];

  const performanceMetrics = [
    { value: 'revenue', label: 'Revenue Generated', icon: DollarSign },
    { value: 'conversions', label: 'Conversions', icon: Target },
    { value: 'referrals', label: 'Referrals Sent', icon: Users },
    { value: 'commission', label: 'Commission Earned', icon: Award }
  ];

  useEffect(() => {
    loadPerformanceData();
  }, [selectedPeriod]);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);

      // Get date range based on selected period
      const dateRange = getDateRange(selectedPeriod);
      
      // Load affiliate performance data
      const { data: performanceData, error: performanceError } = await supabase
        .from('affiliate_performance')
        .select(`
          *,
          profiles (id, name, email, company_name)
        `)
        .gte('month_year', dateRange.start)
        .lte('month_year', dateRange.end)
        .order('revenue_generated', { ascending: false });

      if (performanceError) throw performanceError;

      // Load all active affiliates
      const { data: affiliatesData, error: affiliatesError } = await supabase
        .from('profiles')
        .select('id, name, email, company_name')
        .eq('role', 'affiliate')
        .eq('status', 'active');

      if (affiliatesError) throw affiliatesError;

      // Calculate stats
      const calculatedStats = calculateStats(performanceData || [], affiliatesData || []);
      
      setPerformanceData(performanceData || []);
      setAffiliates(affiliatesData || []);
      setStats(calculatedStats);
    } catch (error) {
      console.error('Error loading performance data:', error);
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (period: string) => {
    const now = new Date();
    
    switch (period) {
      case 'current_month':
        return {
          start: format(startOfMonth(now), 'yyyy-MM'),
          end: format(endOfMonth(now), 'yyyy-MM')
        };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return {
          start: format(startOfMonth(lastMonth), 'yyyy-MM'),
          end: format(endOfMonth(lastMonth), 'yyyy-MM')
        };
      case 'last_3_months':
        return {
          start: format(startOfMonth(subMonths(now, 3)), 'yyyy-MM'),
          end: format(endOfMonth(now), 'yyyy-MM')
        };
      case 'last_6_months':
        return {
          start: format(startOfMonth(subMonths(now, 6)), 'yyyy-MM'),
          end: format(endOfMonth(now), 'yyyy-MM')
        };
      case 'year_to_date':
        return {
          start: format(new Date(now.getFullYear(), 0, 1), 'yyyy-MM'),
          end: format(endOfMonth(now), 'yyyy-MM')
        };
      default:
        return {
          start: format(startOfMonth(now), 'yyyy-MM'),
          end: format(endOfMonth(now), 'yyyy-MM')
        };
    }
  };

  const calculateStats = (performanceData: any[], affiliatesData: any[]): PerformanceStats => {
    const totalReferrals = performanceData.reduce((sum, p) => sum + (p.referrals_sent || 0), 0);
    const totalConversions = performanceData.reduce((sum, p) => sum + (p.conversions || 0), 0);
    const totalRevenue = performanceData.reduce((sum, p) => sum + (p.revenue_generated || 0), 0);
    const totalCommissions = performanceData.reduce((sum, p) => sum + (p.commission_earned || 0), 0);
    
    const activeAffiliateIds = new Set(performanceData.map(p => p.affiliate_id));
    const activeAffiliates = activeAffiliateIds.size;
    
    const averageConversionRate = totalReferrals > 0 ? (totalConversions / totalReferrals) * 100 : 0;
    
    // Get top performers by revenue
    const affiliatePerformance = affiliatesData.map(affiliate => {
      const performance = performanceData.filter(p => p.affiliate_id === affiliate.id);
      const revenue = performance.reduce((sum, p) => sum + (p.revenue_generated || 0), 0);
      return { ...affiliate, totalRevenue: revenue };
    });
    
    const topPerformers = affiliatePerformance
      .filter(a => a.totalRevenue > 0)
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 5);

    return {
      totalAffiliates: affiliatesData.length,
      activeAffiliates,
      totalReferrals,
      totalConversions,
      totalRevenue,
      totalCommissions,
      averageConversionRate,
      topPerformers
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex gap-4 items-center">
        <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
          <SelectTrigger className="w-48">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {timePeriods.map(period => (
              <SelectItem key={period.value} value={period.value}>
                {period.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={selectedMetric} onValueChange={setSelectedMetric}>
          <SelectTrigger className="w-48">
            <BarChart3 className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {performanceMetrics.map(metric => (
              <SelectItem key={metric.value} value={metric.value}>
                {metric.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium">Total Affiliates</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.totalAffiliates}</p>
            <p className="text-xs text-muted-foreground">
              {stats.activeAffiliates} active this period
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold mt-1">{formatCurrency(stats.totalRevenue)}</p>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(stats.totalCommissions)} in commissions
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium">Conversions</span>
            </div>
            <p className="text-2xl font-bold mt-1">{stats.totalConversions}</p>
            <p className="text-xs text-muted-foreground">
              from {stats.totalReferrals} referrals
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Conversion Rate</span>
            </div>
            <p className="text-2xl font-bold mt-1">{formatPercentage(stats.averageConversionRate)}</p>
            <p className="text-xs text-muted-foreground">
              Average across all affiliates
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5" />
            Top Performing Affiliates
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.topPerformers.length > 0 ? (
            <div className="space-y-4">
              {stats.topPerformers.map((affiliate, index) => (
                <div key={affiliate.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="w-8 h-8 rounded-full flex items-center justify-center">
                      {index + 1}
                    </Badge>
                    <div>
                      <p className="font-medium">{affiliate.name}</p>
                      <p className="text-sm text-muted-foreground">{affiliate.email}</p>
                      {affiliate.company_name && (
                        <p className="text-xs text-muted-foreground">{affiliate.company_name}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">
                      {formatCurrency(affiliate.totalRevenue)}
                    </p>
                    <p className="text-xs text-muted-foreground">Revenue Generated</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Award className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No performance data available for the selected period</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Detailed Performance Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          {performanceData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Affiliate</th>
                    <th className="text-right p-2">Period</th>
                    <th className="text-right p-2">Referrals</th>
                    <th className="text-right p-2">Conversions</th>
                    <th className="text-right p-2">Revenue</th>
                    <th className="text-right p-2">Commission</th>
                    <th className="text-center p-2">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceData.map((data) => (
                    <tr key={data.id} className="border-b hover:bg-muted/50">
                      <td className="p-2">
                        <div>
                          <p className="font-medium">{data.profiles?.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{data.profiles?.email}</p>
                        </div>
                      </td>
                      <td className="text-right p-2">{data.month_year}</td>
                      <td className="text-right p-2">{data.referrals_sent}</td>
                      <td className="text-right p-2">{data.conversions}</td>
                      <td className="text-right p-2 font-medium text-green-600">
                        {formatCurrency(data.revenue_generated)}
                      </td>
                      <td className="text-right p-2 font-medium">
                        {formatCurrency(data.commission_earned)}
                      </td>
                      <td className="text-center p-2">
                        {data.commission_paid ? (
                          <Badge variant="secondary" className="bg-green-100 text-green-800">
                            Paid
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                            Pending
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <PieChart className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No detailed performance data available</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliatePerformance;